/**
 * Loudoun (Virginia) chapter media + lockup copy.
 *
 * Main loop is the Loudoun feature lede (`lede-desktop` / `lede-mobile` on
 * the feature S3 prefix) — larger cut than title HED 5 / `bl-suburb`. Hed
 * SVGs also live on that prefix; stills under the hub loudoun prefix.
 *
 * Re-sync local stills with:
 *   aws --profile voxmedia s3 sync \
 *     apps/duet-csk-theverge/src/stories/data-centers-hub/assets/loudoun/ \
 *     s3://assets.sbnation.com/csk/uploads/verge-features/data-centers/data-centers-hub/loudoun/ \
 *     --acl public-read --exclude "*" --include "*.webp" --include "*.svg" \
 *     --exclude "*.orig.*"
 */

import { hubS3Asset } from "@story/data-centers-hub/config/hubS3";
import { type TitleMedia } from "@story/data-centers-hub/config/titleAssets";
import { ledeAssets } from "@story/data-centers-loudoun/config/ledeAssets";

/** Bust browser/S3 caches after replacing stills at the same keys. */
const V = "20261026c";

export function hubLoudounAsset(path: string): string {
  return hubS3Asset("loudoun", path, V);
}

/** Loudoun yellow — primary chrome once the chapter is revealed. */
export const LOUDOUN_YELLOW = "#F8FF32";

/** WP node for the Loudoun feature — share / comment meta on the chapter plate. */
export const LOUDOUN_FEATURE_NODE_ID = "dmcyOmN1c3RvbVN0b3J5Ojk3NTU5Nw==";

/**
 * Lockup fallbacks when the Loudoun WP node is missing. Live values come from
 * `authors` / `additionalBylines` / `dek.plaintext` via `resolveLockupCopy`
 * (same fields as the Loudoun `LedeContainer`).
 */
export const loudounCopy = {
  bylineLabel: "by",
  bylineName: "Lauren Feiner",
  photoBylineLabel: "photos by",
  /** Credits / WP use Stephen; design comps sometimes mistype as Steven. */
  photoBylineName: "Stephen Voss",
  lede: "If you want to know what the data center occupation looks like, there’s one place where it already arrived — 20 years ago.",
  hedAlt: "Is this the future of America?",
  shareTitle: "Is this the future of America?",
} as const;

export const loudounSlideMedia = {
  /** Full-stage underlay — same encode as the Loudoun feature article lede. */
  video: {
    kind: "video",
    src: ledeAssets.videoDesktop,
    poster: ledeAssets.videoPoster,
    srcMobile: ledeAssets.videoMobile,
    posterMobile: ledeAssets.videoPosterMobile,
  },
  /** Article hed SVG from the Loudoun feature (smaller placement than title). */
  hed: ledeAssets.hedDesktop,
  /** Narrow lockup — stacked lines for the mobile chapter. */
  hedMobile: hubLoudounAsset("hed-mobile.svg"),
  left: {
    kind: "image",
    src: hubLoudounAsset("left.webp"),
  },
  br: {
    kind: "image",
    src: hubLoudounAsset("br.webp"),
  },
} as const satisfies {
  video: TitleMedia;
  hed: string;
  hedMobile: string;
  left: TitleMedia;
  br: TitleMedia;
};
